import React from 'react';

import type { MessageDescriptor } from 'react-intl';

import { aiConfigItemMessages, aiMessages } from '@atlaskit/editor-common/ai-messages';
import ChangeToneIcon from '@atlaskit/icon/core/microphone';
import { fg } from '@atlaskit/platform-feature-flags';

import type {
	ChangeToneBusinessCasualIntentSchema,
	ChangeToneEducationalIntentSchema,
	ChangeToneEmpatheticIntentSchema,
	ChangeToneNeutralIntentSchema,
	ChangeToneProfessionalIntentSchema,
} from '../../../../../types/prompt-request-types';
import { ToneCasualIcon } from '../../../../icons/prompt/toneCasual';
import { ToneEducationalIcon } from '../../../../icons/prompt/toneEducational';
import { ToneEmpatheticIcon } from '../../../../icons/prompt/toneEmpathetic';
import { ToneNeutralIcon } from '../../../../icons/prompt/toneNeutral';
import { ToneProfessionalIcon } from '../../../../icons/prompt/toneProfessional';
import type { ConvoAiChangeToneFeatureIds } from '../../../../utils/request/types';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemFactoryOptions,
	EditorPluginAIConfigItemWithOptionsFactory,
	EditorPluginAIConfigItem,
	EditorPluginAIConfigItemFactory,
} from '../../config-items';

function createChangeToneOption({
	key,
	title,
	description,
	intentSchemaId,
	featureId,
	icon,
	shortTitle,
	adfPrompt,
	factoryOptions,
}: Pick<EditorPluginAIConfigItem, 'key' | 'title' | 'description' | 'icon'> & {
	adfPrompt: boolean;
	factoryOptions?: EditorPluginAIConfigItemFactoryOptions;
	featureId: ConvoAiChangeToneFeatureIds;
	intentSchemaId:
		| ChangeToneBusinessCasualIntentSchema
		| ChangeToneEducationalIntentSchema
		| ChangeToneEmpatheticIntentSchema
		| ChangeToneNeutralIntentSchema
		| ChangeToneProfessionalIntentSchema;
	shortTitle: MessageDescriptor;
}): EditorPluginAIConfigItem {
	return {
		key,
		title,
		description,
		icon,
		showInRefineDropdown: true,
		intentSchemaId,
		featureId,
		adfPrompt,
		requiresTextualContent: adfPrompt,
		nestingConfig: {
			id: 'change-tone-to',
			parentTestId: 'change-tone-to',
			parentTitle: aiMessages.changeToneToolbarDropdownIconTitle,
			shortTitle,
			icon: () => <ChangeToneIcon label="" spacing="spacious" />,
		},
		actions: {
			empty: [
				createInsertBelow({ appearance: 'secondary', factoryOptions }),
				createReplace({ appearance: 'primary' }),
			],
			selection: [
				createInsertBelow({ appearance: 'secondary', factoryOptions }),
				createReplace({ appearance: 'primary' }),
			],
		},
		getBackendModel: () => {
			return 'assistance-service';
		},
	};
}

export const changeToneToNeutral: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = 'change_tone_neutral';
	const adfPrompt = false;

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_NEUTRAL,
		title: aiConfigItemMessages.changeToneNeutralToneTitle,
		shortTitle: aiConfigItemMessages.changeToneNeutralToneShortTitle,
		description: aiConfigItemMessages.changeToneNeutralToneDescription,
		intentSchemaId: 'change_tone_neutral_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneNeutralIcon />,
		factoryOptions: options,
	});
};

export const changeToneToNeutralWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToNeutral(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToProfessional: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = fg(
		'platform_editor_ai_adf_prompts_in_all_products',
	)
		? 'change_tone_professional_adf_gpt41mini'
		: 'change_tone_professional';
	const adfPrompt = fg('platform_editor_ai_adf_prompts_in_all_products');

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_PROFESSIONAL,
		title: aiMessages.moreProfessionalToneToolbarDropdownIconTitle,
		shortTitle: aiConfigItemMessages.changeToneProfessionalToneShortTitle,
		description: aiConfigItemMessages.changeToneProfessionalToneDescription,
		intentSchemaId: 'change_tone_professional_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneProfessionalIcon />,
		factoryOptions: options,
	});
};

export const changeToneToProfessionalWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToProfessional(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToCasual: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = fg(
		'platform_editor_ai_adf_prompts_in_all_products',
	)
		? 'change_tone_casual_adf_gpt41mini'
		: 'change_tone_casual';
	const adfPrompt = fg('platform_editor_ai_adf_prompts_in_all_products');

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_CASUAL,
		title: aiMessages.moreCasualToneToolbarDropdownIconTitle,
		shortTitle: aiConfigItemMessages.changeToneCasualToneShortTitle,
		description: aiConfigItemMessages.changeToneCasualToneDescription,
		intentSchemaId: 'change_tone_business_casual_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneCasualIcon />,
		factoryOptions: options,
	});
};

export const changeToneToCasualWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToCasual(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToEducational: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = fg(
		'platform_editor_ai_adf_prompts_in_all_products',
	)
		? 'change_tone_educational_adf_gpt41mini'
		: 'change_tone_educational';
	const adfPrompt = fg('platform_editor_ai_adf_prompts_in_all_products');

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EDUCATIONAL,
		title: aiConfigItemMessages.changeToneEducationalToneTitle,
		shortTitle: aiConfigItemMessages.changeToneEducationalToneShortTitle,
		description: aiConfigItemMessages.changeToneEducationalToneDescription,
		intentSchemaId: 'change_tone_educational_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneEducationalIcon />,
		factoryOptions: options,
	});
};

export const changeToneToEducationalWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToEducational(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToEmpathetic: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = fg(
		'platform_editor_ai_adf_prompts_in_all_products',
	)
		? 'change_tone_empathetic_adf_gpt41mini'
		: 'change_tone_empathetic';
	const adfPrompt = fg('platform_editor_ai_adf_prompts_in_all_products');

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EMPATHETIC,
		title: aiMessages.moreEmpatheticToneToolbarDropdownIconTitle,
		shortTitle: aiConfigItemMessages.changeToneEmpatheticToneShortTitle,
		description: aiConfigItemMessages.changeToneEmpatheticToneDescription,
		intentSchemaId: 'change_tone_empathetic_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneEmpatheticIcon />,
		factoryOptions: options,
	});
};

export const changeToneToEmpatheticWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToEmpathetic(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});
